import React, { useState } from 'react';
import { DateTime } from 'luxon';

import { formatToDealerTime } from 'utils/dateUtils';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import MarkStateDropdownMAIQ, { ManualRunState } from 'app/components/MarkStateDropdownMAIQ';
import MetricValue from './MetricValue';

interface StatusHistoryItem {
  status: string;
  timestamp: string;
  reason?: string;
  _id: string;
}

interface FileChecksum {
  algorithm?: string;
  value?: string;
}

const formatFileChecksum = (checksum: unknown): string | null => {
  if (!checksum) return null;
  if (typeof checksum === 'string') {
    const trimmed = checksum.trim();
    return trimmed || null;
  }
  if (typeof checksum !== 'object') return null;

  const record = checksum as FileChecksum;
  const algorithm = typeof record.algorithm === 'string' ? record.algorithm.trim() : '';
  const value = typeof record.value === 'string' ? record.value.trim() : '';
  if (algorithm && value) return `${algorithm}: ${value}`;
  return value || algorithm || null;
};

interface ActionMetadata {
  dealer_id?: string;
  lead_type?: string;
  ingestion_type?: string;
  ingestion_provider?: string;
  filename?: string;
  file_checksum?: FileChecksum | string;
  provider?: string;
  run_status?: string;
  metrics?: {
    [key: string]: unknown;
    no_of_records?: number;
    content_type?: string;
  };
  runId?: string;
  actionId?: string;
  dataType?: string;
  expiresAt?: string;
  conversionMetadata?: any;
  timestamp?: string;
  size?: number;
  mapperName?: string;
  no_of_mapped_data?: number;
  no_of_records?: number;
  contentType?: string;
  originalType?: string;
  airflow_logs?: any[];
  query?: unknown;
}

interface Action {
  _id: string;
  function_name: string;
  parameters: any;
  status: string;
  status_history: StatusHistoryItem[];
  metadata: ActionMetadata;
  is_data_available_to_download: boolean;
  data_type?: string;
}

const RECORD_RETRY_ACTIONS = new Set(['identifytheshopper', 'processtheleads']);

const failedLeadCount = (action: Action): number => {
  const raw = (action.metadata?.metrics as Record<string, unknown> | undefined)?.failed_leads;
  const value = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const canRetryFailedRecords = (action: Action): boolean =>
  RECORD_RETRY_ACTIONS.has(String(action.function_name || '').toLowerCase()) &&
  failedLeadCount(action) > 0;

const actionLabel = (name: string): string =>
  name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, first => first.toUpperCase());

interface ActionsAccordionComponentProps {
  actions: Action[];
  title?: string;
  runId?: string; // Add runId prop
  onDownload?: (runId: string, actionId: string, action: Action, format?: string) => void;
  onFailedLeadsClick?: (action: Action) => void;
  onMarkState?: (action: Action, state: ManualRunState) => Promise<void>;
  onRetryAction?: (action: Action) => Promise<void>;
  stateBusy?: boolean;
}

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'running':
      return <PlayArrowIcon sx={{ color: '#2196f3', fontSize: 20 }} />;
    case 'pending':
      return <PendingIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
    case 'failed':
      return <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />;
    default:
      return <PendingIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return '#4caf50';
    case 'running':
      return '#2196f3';
    case 'pending':
      return '#ff9800';
    case 'failed':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

const formatDuration = (startTime: string, endTime: string) => {
  const start = DateTime.fromISO(startTime);
  const end = DateTime.fromISO(endTime);
  const duration = end.diff(start);
  
  const minutes = Math.floor(duration.as('minutes'));
  const seconds = Math.floor(duration.as('seconds') % 60);
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export const ActionsAccordionMAIQ: React.FC<ActionsAccordionComponentProps> = ({
  actions,
  title = 'Pipeline Actions',
  runId,
  onDownload,
  onFailedLeadsClick,
  onMarkState,
  onRetryAction,
  stateBusy,
}) => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);

  const getActionDataType = (action: Action) =>
    String(
      action?.data_type ||
        action?.parameters?.data_type ||
        action?.parameters?.content_type ||
        action?.metadata?.dataType ||
        action?.metadata?.contentType ||
        action?.metadata?.originalType ||
        '',
    ).toLowerCase();

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleDefaultDownload = (action: Action, event?: React.MouseEvent<HTMLElement>) => {
    // Prevent accordion expansion
    if (event) {
      event.stopPropagation();
    }
    
    console.log('=== ACTIONS ACCORDION DEFAULT DOWNLOAD CLICKED ===');
    console.log('Action clicked:', action);
    console.log('Action ID:', action._id);
    console.log('Run ID:', runId);
    console.log('Action parameters:', action.parameters);
    console.log('onDownload function available:', !!onDownload);
    
    // Get default format from action parameters
    const defaultFormat = getActionDataType(action) || 'csv';
    console.log('Default format from parameters:', defaultFormat);
    
    if (onDownload && action._id && runId) {
      console.log('✅ Calling onDownload with default format:', { runId, actionId: action._id, action, format: defaultFormat });
      onDownload(runId, action._id, action, defaultFormat);
    } else {
      console.error('❌ Missing required data for download:');
      console.error('- onDownload function:', !!onDownload);
      console.error('- action ID:', action._id);
      console.error('- run ID:', runId);
      console.error('Full action object:', action);
      alert('Error: Missing required information for download.');
    }
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>, action: Action) => {
    event.stopPropagation();
    if (getActionDataType(action) === 'zip') {
      return;
    }
    setDownloadMenuAnchor(event.currentTarget);
    setSelectedAction(action);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setSelectedAction(null);
  };

  const handleDownload = (format: string) => {
    console.log('=== ACTIONS ACCORDION FORMAT DOWNLOAD CLICKED ===');
    console.log('Action clicked:', selectedAction);
    console.log('Format selected:', format);
    console.log('Action ID:', selectedAction?._id);
    console.log('Run ID:', runId);
    console.log('Action parameters:', selectedAction?.parameters);
    console.log('onDownload function available:', !!onDownload);
    
    if (onDownload && selectedAction?._id && runId) {
      console.log('✅ Calling onDownload with format:', { runId, actionId: selectedAction._id, action: selectedAction, format });
      onDownload(runId, selectedAction._id, selectedAction, format);
    } else {
      console.error('❌ Missing required data for download:');
      console.error('- onDownload function:', !!onDownload);
      console.error('- action ID:', selectedAction?._id);
      console.error('- run ID:', runId);
      console.error('Full action object:', selectedAction);
      alert('Error: Missing required information for download.');
    }
    
    handleDownloadMenuClose();
  };

  const handleDownloadButtonClick = (event: React.MouseEvent<HTMLElement>, action: Action) => {
    event.stopPropagation();
    
    // Check if click was on the dropdown arrow part
    const target = event.target as HTMLElement;
    const isDropdownClick = target.closest('[data-dropdown="true"]') || 
                           target.classList.contains('MuiSvgIcon-root') && 
                           target.textContent === 'keyboard_arrow_down';
    
    if (isDropdownClick) {
      // Open dropdown menu
      setDownloadMenuAnchor(event.currentTarget);
      setSelectedAction(action);
    } else {
      // Direct download with default format
      handleDefaultDownload(action, event);
    }
  };

  const renderStatusHistory = (statusHistory: StatusHistoryItem[]) => {
    // Check if statusHistory exists and is not null/undefined
    if (!statusHistory || !Array.isArray(statusHistory) || statusHistory.length === 0) {
      return null;
    }

    return (
      <Box sx={{ borderTop: '1px solid #e7edf4', pt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
          Status History
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {statusHistory.map((item, index) => {
            const isLast = index === statusHistory.length - 1;
            const nextItem = statusHistory[index + 1];
            const duration = nextItem 
              ? formatDuration(item.timestamp, nextItem.timestamp)
              : null;

            return (
              <Box key={item._id} sx={{ position: 'relative', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Status Icon */}
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusIcon(item.status)}
                  </Box>
                  
                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </Typography>
                      {duration && (
                        <Chip
                          label={duration}
                          size="small"
                          sx={{
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            height: '20px',
                          }}
                        />
                      )}
                    </Box>
                    
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      {item.timestamp ? formatToDealerTime(item.timestamp) : ''}
                    </Typography>
                    
                    {item.reason && (
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                        {item.reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                {/* Connecting Line */}
                {!isLast && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '10px',
                      top: '24px',
                      width: '2px',
                      height: 'calc(100% + 8px)',
                      backgroundColor: '#e5e7eb',
                      zIndex: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMetadata = (metadata: ActionMetadata, action?: Action) => {
    // Check if metadata exists and is not null/undefined
    if (!metadata) {
      return null;
    }

    const metadataItems: Array<{ label: string; value: unknown; isMetric?: boolean; copyable?: boolean; fullWidth?: boolean; onClick?: () => void }> = [];
    
    // Only show the fields that were mentioned in the requirements
    if (metadata.provider) metadataItems.push({ label: 'Provider', value: metadata.provider });
    if (metadata.lead_type) metadataItems.push({ label: 'Lead Type', value: metadata.lead_type });
    if (metadata.ingestion_type) metadataItems.push({ label: 'Ingestion Type', value: metadata.ingestion_type });
    if (metadata.ingestion_provider) metadataItems.push({ label: 'Ingestion Provider', value: metadata.ingestion_provider });
    if (metadata.filename) metadataItems.push({ label: 'File Name', value: metadata.filename, fullWidth: true, copyable: true });
    const fileChecksum = formatFileChecksum(metadata.file_checksum);
    if (fileChecksum) metadataItems.push({ label: 'File Checksum', value: fileChecksum, fullWidth: true, copyable: true });
    if (metadata.run_status) metadataItems.push({ label: 'Run Status', value: metadata.run_status });
    if (metadata.dataType) metadataItems.push({ label: 'Data Type', value: metadata.dataType });
    if (metadata.mapperName) metadataItems.push({ label: 'Mapper Name', value: metadata.mapperName });
    if (metadata.size) metadataItems.push({ label: 'Size', value: `${(metadata.size / 1024).toFixed(2)} KB` });
    if (metadata.no_of_records) metadataItems.push({ label: 'Records', value: metadata.no_of_records.toString() });
    if (metadata.no_of_mapped_data) metadataItems.push({ label: 'Mapped Records', value: metadata.no_of_mapped_data.toString() });
    if (metadata.expiresAt) metadataItems.push({ 
      label: 'Expires At', 
      value: formatToDealerTime(metadata.expiresAt),
    });

    // Dynamically render metrics if available
    if (metadata.metrics && typeof metadata.metrics === 'object') {
      Object.entries(metadata.metrics).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Convert snake_case to Title Case (e.g., "no_of_leads" -> "No Of Leads")
          const label = key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          const numericValue = typeof value === 'number' ? value : Number(value);
          const canOpenFailed =
            key === 'failed_leads' &&
            numericValue > 0 &&
            Boolean(action && canRetryFailedRecords(action) && onFailedLeadsClick);
          
          metadataItems.push({ 
            label, 
            value,
            isMetric: true,
            onClick: canOpenFailed && action ? () => onFailedLeadsClick?.(action) : undefined,
          });
        }
      });
    }

    if (metadataItems.length === 0) return null;

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
          Details & metrics
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.25 }}>
          {metadataItems.map((item, index) => (
            <Box key={index} sx={{ border: '1px solid #e7edf4', borderRadius: '6px', backgroundColor: '#fff', px: 1.5, py: 1.25, minWidth: 0, gridColumn: item.fullWidth ? '1 / -1' : undefined }}>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
                {item.label}
              </Typography>
              {item.isMetric || item.copyable ? (
                <MetricValue label={item.label} value={item.value} onClick={item.onClick} />
              ) : item.onClick ? (
                <Tooltip title="View failed records">
                  <Button
                    size="small"
                    variant="text"
                    onClick={item.onClick}
                    sx={{
                      textTransform: 'none',
                      color: '#b45309',
                      fontWeight: 600,
                      textDecoration: 'underline',
                      minWidth: 0,
                      p: 0,
                      justifyContent: 'flex-start',
                    }}
                  >
                    {String(item.value)}
                  </Button>
                </Tooltip>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', overflowWrap: 'anywhere' }}>
                  {String(item.value)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const getFormattedQuery = (query: unknown): string | null => {
    if (query === undefined || query === null) {
      return null;
    }

    if (typeof query === 'object') {
      try {
        return JSON.stringify(query, null, 2);
      } catch (error) {
        return String(query);
      }
    }

    if (typeof query === 'string') {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) {
        return null;
      }

      try {
        const parsedQuery = JSON.parse(trimmedQuery);
        return JSON.stringify(parsedQuery, null, 2);
      } catch (error) {
        return trimmedQuery;
      }
    }

    return String(query);
  };

  const renderActionQuery = (query: unknown) => {
    const formattedQuery = getFormattedQuery(query);
    if (!formattedQuery) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
          Query
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            color: '#1f2937',
            fontSize: '0.8rem',
            lineHeight: 1.5,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {formattedQuery}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {Array.isArray(actions) ? actions.length : 0} steps
        </Typography>
      </Box>
      
      {(!actions || !Array.isArray(actions) || actions.length === 0) ? (
        <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
          No actions available
        </Typography>
      ) : (
        actions.map((action, index) => {
        // Skip rendering if action is invalid or missing required properties
        if (!action || !action._id || !action.function_name) {
          return null;
        }

        return (
          <Accordion
            key={action._id}
            expanded={expandedAccordion === action._id}
            onChange={handleAccordionChange(action._id)}
            sx={{
              mb: 1,
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '0 0 8px',
              },
            }}
          >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#fff',
              minHeight: 64,
              '&.Mui-expanded': { minHeight: 64 },
              '& .MuiAccordionSummary-content': { my: 1.5 },
              '& .MuiAccordionSummary-content.Mui-expanded': { my: 1.5 },
              '&:hover': { backgroundColor: '#f8fafc' },
              borderBottom: expandedAccordion === action._id ? '1px solid #e5e7eb' : 'none',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, width: '100%', pr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: '1 1 220px' }}>
                <Box component="span" sx={{ width: 28, height: 28, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', borderRadius: '50%', fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                  {index + 1}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', overflowWrap: 'anywhere' }}>
                  {actionLabel(action.function_name)}
                </Typography>
                <Chip
                  label={action.status.charAt(0).toUpperCase() + action.status.slice(1)}
                  size="small"
                  sx={{ height: 22, flexShrink: 0, fontSize: 11, backgroundColor: getStatusColor(action.status) + '15', color: getStatusColor(action.status), fontWeight: 600 }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ml: 'auto' }}>
                <Box sx={{ width: 108, display: 'flex', justifyContent: 'flex-end' }}>
                 {action.status.toLowerCase() === 'failed' && onRetryAction ? (
                   <Button size="small" variant="contained" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} disabled={stateBusy} onClick={event => {
                     event.stopPropagation();
                     void onRetryAction(action);
                   }} onMouseDown={event => event.stopPropagation()} sx={{ textTransform: 'none', height: 28, boxShadow: 'none', backgroundColor: '#0F2A52', fontSize: 12 }}>
                     Retry
                   </Button>
                 ) : canRetryFailedRecords(action) && onFailedLeadsClick ? (
                   <Button
                     size="small"
                     variant="outlined"
                     disabled={stateBusy}
                     onClick={event => {
                       event.stopPropagation();
                       onFailedLeadsClick(action);
                     }}
                     onMouseDown={event => event.stopPropagation()}
                     sx={{
                       textTransform: 'none',
                       height: 28,
                       fontSize: 12,
                       color: '#b45309',
                       borderColor: '#f3d7b4',
                       backgroundColor: '#fffbf5',
                       '&:hover': { borderColor: '#d6ab77', backgroundColor: '#fff7ed' },
                     }}
                   >
                     View failed ({failedLeadCount(action)})
                   </Button>
                 ) : null}
                </Box>
                <Box sx={{ width: 56, display: 'flex', justifyContent: 'center' }}>
                 {action.is_data_available_to_download && (
                   <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #dbe2ea', borderRadius: '6px', backgroundColor: '#fff' }}>
                     <Tooltip title="Download Data (Default Format)">
                       <IconButton
                         size="small"
                         aria-label={`Download ${actionLabel(action.function_name)} data`}
                         onClick={(e) => handleDefaultDownload(action, e)}
                         sx={{
                           color: '#52627a',
                           borderRadius: '5px',
                           width: 28,
                           height: 26,
                           '&:hover': {
                             backgroundColor: '#f4f7fb',
                           },
                         }}
                       >
                         <DownloadIcon fontSize="small" />
                       </IconButton>
                     </Tooltip>
                     {getActionDataType(action) !== 'zip' && (
                       <Tooltip title="More Download Options">
                         <IconButton
                           size="small"
                           aria-label={`Download options for ${actionLabel(action.function_name)}`}
                           onClick={(e) => handleDropdownClick(e, action)}
                           data-dropdown="true"
                           sx={{
                             color: '#52627a',
                             borderRadius: '5px',
                             width: 22,
                             height: 26,
                             '&:hover': {
                               backgroundColor: '#f4f7fb',
                             },
                           }}
                         >
                           <KeyboardArrowDownIcon fontSize="small" />
                         </IconButton>
                       </Tooltip>
                     )}
                   </Box>
                 )}
                </Box>
                {onMarkState && (
                  <MarkStateDropdownMAIQ entityId={`action-${action._id}`} disabled={stateBusy} onChange={state => onMarkState(action, state)} />
                )}
               </Box>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 2.5, backgroundColor: '#fbfcfe', display: 'grid', gap: 2.5 }}>
            {renderActionQuery(action.metadata?.query)}
            {renderMetadata(action.metadata, action)}
            {renderStatusHistory(action.status_history)}
          </AccordionDetails>
        </Accordion>
        );
      })
      )}
      
      {/* Download Format Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 120,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        <MenuItem 
          onClick={() => handleDownload('json')}
          sx={{
            fontSize: '0.875rem',
            py: 1.5,
            '&:hover': {
              backgroundColor: 'rgba(15, 42, 82, 0.04)',
            },
          }}
        >
          <DownloadIcon fontSize="small" sx={{ mr: 1, color: '#0F2A52' }} />
          Download JSON
        </MenuItem>
        <MenuItem 
          onClick={() => handleDownload('csv')}
          sx={{
            fontSize: '0.875rem',
            py: 1.5,
            '&:hover': {
              backgroundColor: 'rgba(15, 42, 82, 0.04)',
            },
          }}
        >
          <DownloadIcon fontSize="small" sx={{ mr: 1, color: '#0F2A52' }} />
          Download CSV
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ActionsAccordionMAIQ;
